import * as React from "react";
import { Network, ParentNetworkMetadata } from "../../types";
import { PageHeader } from "~/components/PageHeader";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { Badge, BadgeVariants } from "~/components/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor } from "~/components/Anchor";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { NetworkUserEvents } from "~/modules/analytics/events/networks";
import { addUtmTrackingToUrl } from "~/utils/addUtmTrackingToUrl";
import { Link } from "~/components/Link";
import { getNetworkURL } from "~/routes";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { useFeatureFlag } from "~/modules/feature_flag/hooks";
import { Networks } from "~/schema";
import { DescriptionDisplay } from "~/components/DescriptionDisplay";

// // // //

const LIQUID_NETWORK_SLUG = Networks.liquid;

export function NetworkHeader(props: {
    network: Network;
    hasChildNetworks: boolean;
    parentNetwork: ParentNetworkMetadata | null;
}) {
    const { network, hasChildNetworks, parentNetwork } = props;

    const enabledExperimentalFeatures = useFeatureFlag(
        FeatureNames.enableExperimentalFeatures,
    );

    const isLiquidNetwork = network.slug === LIQUID_NETWORK_SLUG;

    // Define a boolean to determine if we should show the badges
    const hasBadges =
        network.asset_class_stats.length > 0 ||
        isLiquidNetwork ||
        (parentNetwork && enabledExperimentalFeatures);

    return (
        <>
            <PageHeader
                title={
                    <>
                        {network.name}
                        {hasChildNetworks && " (All Chains)"}
                    </>
                }
                icon={
                    <ImageLoader
                        src={network.icon_url || RWA_COIN_ICON}
                        alt={`${network.name} Icon`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-10 w-10 min-w-10 rounded-full"
                    />
                }
            />

            {hasBadges && (
                <div className="flex items-center gap-3 flex-wrap">
                    {network.website !== null && (
                        <Anchor
                            href={addUtmTrackingToUrl(network.website)}
                            openInNewTab
                            analyticsLabel={
                                NetworkUserEvents.ClickNetworkIndividualWebsite
                            }
                        >
                            <Badge
                                label={
                                    <>
                                        Website
                                        <FontAwesomeIcon
                                            icon={faExternalLink}
                                            className="h-3 ml-1.5"
                                        />
                                    </>
                                }
                                variant={BadgeVariants.blue}
                                hover
                                className="px-3 py-0.5"
                                size="sm"
                            />
                        </Anchor>
                    )}

                    {parentNetwork && enabledExperimentalFeatures && (
                        <Link
                            href={getNetworkURL({
                                networkSlug: parentNetwork.slug,
                            })}
                            analyticsLabel={
                                NetworkUserEvents.ClickParentNetworkLink
                            }
                            analyticsEventProperties={{
                                network_slug: parentNetwork.slug,
                                network_name: parentNetwork.name,
                            }}
                        >
                            <Badge
                                label={
                                    <>
                                        Parent Network:&nbsp;
                                        {parentNetwork.name}
                                        <FontAwesomeIcon
                                            icon={faExternalLink}
                                            className="h-3 ml-1.5"
                                        />
                                    </>
                                }
                                variant={BadgeVariants.blue}
                                hover
                                className="px-3 py-0.5"
                                size="sm"
                            />
                        </Link>
                    )}

                    {/* Render "Privacy Chain" badge ONLY for Liquid Network */}
                    {isLiquidNetwork && (
                        <Badge
                            label="Privacy Chain"
                            variant={BadgeVariants.gray}
                            size="sm"
                        />
                    )}
                </div>
            )}

            <DescriptionDisplay description={network.description} />
        </>
    );
}
